/**
 * The "Rental Finder" page (`/listings`) — what the header nav's "Rental
 * Finder" link now points at, having been inert since the homepage shipped.
 *
 * Filtering is entirely client-side against the one already-fetched list of
 * published listings (same `GET /api/listings` the homepage uses) — at this
 * company's stated scale (10-30 active listings) there is nothing server-side
 * filtering or pagination would buy that a `useMemo` doesn't already give
 * for free, and it keeps every filter change instant with no network round
 * trip.
 *
 * Layout departs from the homepage/detail-page pattern on purpose: those two
 * pages each have a photo to build a hero around; this page's job is
 * browsing by criteria, not a single image, so the map itself — filtered
 * live alongside the results — is the page's visual center rather than a
 * strip below the fold. The teal toolbar band is the one callback to the
 * rest of the site's signature material (see RentalFinderFilters.tsx).
 */

import { useEffect, useMemo, useState } from 'react';
import type { PublicListingDto } from 'shared';
import { api } from '../../lib/api.js';
import { ListingCard } from './ListingCard.js';
import { ListingsMap } from './ListingsMap.js';
import {
  EMPTY_FILTERS,
  RentalFinderFilters,
  type FinderFilterState,
} from './RentalFinderFilters.js';

function matches(listing: PublicListingDto, filters: FinderFilterState): boolean {
  const price = Number(listing.price);
  if (filters.minPrice !== '' && price < Number(filters.minPrice)) return false;
  if (filters.maxPrice !== '' && price > Number(filters.maxPrice)) return false;
  if (filters.minBedrooms > 0 && listing.bedrooms < filters.minBedrooms) return false;
  if (filters.minBathrooms > 0 && Number(listing.bathrooms) < filters.minBathrooms) return false;
  if (filters.city !== '' && listing.city !== filters.city) return false;
  if (filters.search.trim() !== '') {
    const needle = filters.search.trim().toLowerCase();
    const haystack =
      `${listing.addressLine1} ${listing.city} ${listing.description ?? ''}`.toLowerCase();
    if (!haystack.includes(needle)) return false;
  }
  return true;
}

export function RentalFinder() {
  const [listings, setListings] = useState<PublicListingDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<FinderFilterState>(EMPTY_FILTERS);

  useEffect(() => {
    let cancelled = false;
    void api<{ data: PublicListingDto[] }>('/listings')
      .then((response) => {
        if (!cancelled) setListings(response.data);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load current listings. Please try again shortly.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const cities = useMemo(() => {
    if (!listings) return [];
    return [
      ...new Set(listings.map((l) => l.city).filter((city): city is string => city !== '')),
    ].sort();
  }, [listings]);

  const filtered = useMemo(() => {
    if (!listings) return [];
    return listings.filter((listing) => matches(listing, filters));
  }, [listings, filters]);

  return (
    <div className="public__finder">
      <RentalFinderFilters
        filters={filters}
        onChange={setFilters}
        cities={cities}
        resultCount={filtered.length}
      />

      {error ? (
        <p className="alert alert--error public__finder-error">{error}</p>
      ) : (
        <div className="public__finder-body">
          <div className="public__finder-results">
            {listings === null ? <p className="muted">Loading listings…</p> : null}
            {listings !== null && filtered.length === 0 ? (
              <p className="muted public__finder-empty">
                Nothing matches those filters yet. Try widening your price range or clearing a
                filter.
              </p>
            ) : null}
            {filtered.map((listing) => (
              <ListingCard key={listing.id} listing={listing} />
            ))}
          </div>

          <div className="public__finder-map">
            <ListingsMap listings={filtered} />
          </div>
        </div>
      )}
    </div>
  );
}
